/* eslint-disable import/named */
import { Chart, ChartEvent, defaults } from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
// @ts-ignore
import colours from "styles/_colours.scss";

defaults.font.family = "HCo Gotham";

/**
 * Returns a fresh set of global bar chart options for Chart.js.
 * This ensures that each chart instance receives an isolated config object,
 * preventing unintended shared mutations between charts.
 *
 */
export const globalBarChartOptions = (): BarChartOptions => ({
  responsive: true,
  maintainAspectRatio: false,
  events: ["click", "touchstart", "touchmove"],
  plugins: {
    legend: {
      position: "top",
      align: "end",
      onClick: null,
      labels: {
        pointStyle: "rectRounded",
        usePointStyle: true,
        boxHeight: 10,
      },
    },
    zoom: {
      zoom: {
        pinch: {
          enabled: false,
        },
        wheel: {
          enabled: false,
        },
        mode: "x",
      },
    },
    datalabels: {
      color: "black",
      display: ({ dataset, dataIndex }) => {
        if (!dataset?.status) return false;
        return (
          dataset.data?.[dataIndex] === 0 &&
          dataset.status[dataIndex] === "missing"
        );
      },
      formatter: function (_, context) {
        return context.dataset.noDataText ?? "";
      },
      rotation: 270,
      font: {
        lineHeight: 0,
      },
      clamp: true,
      backgroundColor: () => colours.white,
    },
  },
  elements: {
    bar: {
      borderRadius: 2,
    },
  },
  scales: {
    x: {
      grid: {
        drawOnChartArea: false,
        drawTicks: false,
        tickWidth: 2,
      },
      ticks: {
        font: {
          weight: 400,
        },
        backdropColor: "transparent",
        showLabelBackdrop: true,
        backdropPadding: { top: 0, left: 10, right: 10, bottom: 15 },
        padding: 10,
      },
    },
    y: {
      title: {
        display: true,
        align: "start",
      },
      ticks: {
        autoSkip: true,
        font: {
          weight: 400,
        },
      },
      grid: {
        drawBorder: false,
        lineWidth: 2,
      },
    },
  },
  interaction: {
    mode: "index",
  },
  intersect: true,
});

/**
 * Returns the index of the selected x position.
 */
export const selectedItemIndex = (event: ChartEvent, chart: Chart): number => {
  const { x: position } = getRelativePosition(event, chart);
  return chart.scales.x.getValueForPixel(position);
};

/**
 * Highlights a selected bar on the x-axis by modifying the font weight and backdrop color
 * of the corresponding tick label. Used to visually indicate the selected data point.
 */
export const highlightSelectedItem = (itemIndex: number, chart: Chart) => {
  const scale = chart.options.scales?.x;
  if (!scale || typeof scale !== "object" || !("ticks" in scale)) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { ticks } = scale as { ticks: any };

  ticks.font = (context) => ({
    weight: context?.index === itemIndex ? "600" : "400",
  });

  ticks.backdropColor = (context) =>
    context?.index === itemIndex ? colours.greyTint5 : "transparent";

  chart.update();
};

/**
 * Helper to create a legend item object with given text and color.
 */
const createLegendItem = (
  text: string,
  colour: string,
): BarChartConfigLabel => ({
  text,
  fillStyle: colours[colour],
  pointStyle: "rectRounded",
  usePointStyle: true,
  boxHeight: 10,
  lineWidth: 0,
});

export const offpeakLegend = [
  createLegendItem("peak", "crimson"),
  createLegendItem("off-peak", "green"),
  createLegendItem("peak (estimate)", "pink"),
  createLegendItem("off-peak (estimate)", "greyShade30"),
];

export const forecastPriceLegend = [
  createLegendItem("previous", "yellow"),
  createLegendItem("upcoming", "greyShade30"),
];

export const hourlyUsageBreakdownLegend = [
  createLegendItem("Actual", "green"),
  createLegendItem("Estimate", "pink"),
];
